// NodeMissionTaskChecklist.jsx

import React, { useCallback, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useWorkflowTaskChecklist from 'node/missions/hooks/useWorkflowTaskChecklist';

// Services
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';

// Utilities
import {
  getChecklistActionButtonLabel,
  getChecklistActionIconKey,
  isChecklistSetFieldActionType,
} from 'missions/taskmodal/checklistItemActions';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// CSS
import './NodeMissionTaskChecklist.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Confirm from 'components/confirm/Confirm';
import Icon from 'svg/Icon';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import NodeHiddenFileInputs from 'node/inputs/NodeHiddenFileInputs';
import NodeMissionTaskChecklistActionModal from 'node/missions/mission/task/checklist/NodeMissionTaskChecklistActionModal';

/**
 * NodeMissionTaskChecklist
 *
 * Expand/collapse Task Checklist on a workflow step row. Toggles write the TASK
 * checklist field instance. Offers Confirm when recipe hits 100% on an active step.
 * Optional actions: Set Field on the host record, or attach children onto the TASK.
 *
 * @param {object} task - Run step (includes Task Checklist recipe flags).
 * @param {object} stepNode - Linked TASK node.
 * @param {object|null} [hostNode] - Workflow host record (e.g. PROJECT).
 * @param {object|null} [template] - Current workflow template (preferred recipe source).
 * @param {function} [onHostNodeChange] - Optional local host patch after field saves.
 * @param {object|null} user
 * @param {function} [onProgressChange] - ({ totalItems, completedItems, isComplete }) => void
 * @param {function} [onRequestComplete] - Called when user confirms complete-at-100%.
 * @returns {ReactNode}
 */
const NodeMissionTaskChecklist = React.memo(({
  task,
  stepNode,
  hostNode = null,
  template = null,
  onHostNodeChange = null,
  user = null,
  onProgressChange = null,
  onRequestComplete = null,
}) => {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(true);
  const checklist = useWorkflowTaskChecklist({
    hostNode,
    onProgressChange,
    onRequestComplete,
    stepNode,
    task,
    template,
    user,
  });

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  if (!checklist.recipe.items.length || !checklist.nodeKey) {
    return null;
  }

  const progressLabel = checklist.progress.totalItems > 0
    ? `${checklist.progress.completedItems}/${checklist.progress.totalItems}`
    : '0/0';

  return (
    <div className="node-mission-task-checklist">
      <button
        className="node-mission-task-checklist-toggle"
        style={{ color: theme.foregroundMuted }}
        type="button"
        onClick={handleToggleExpanded}
      >
        <Icon
          color={theme.foregroundMuted}
          height={18}
          iconKey={expanded ? 'keyboard_arrow_down' : 'chevron_right'}
          width={18}
        />
        <span className="node-mission-task-checklist-toggle-label">
          Checklist
        </span>
        <span className="node-mission-task-checklist-rule" aria-hidden="true" />
        <span className="node-mission-task-checklist-progress">
          {progressLabel}
          {checklist.recipe.required ? ' · Required' : ''}
        </span>
      </button>

      {expanded ? (
        <div className="node-mission-task-checklist-items">
          {checklist.snapshotItems.length > 0 ? checklist.snapshotItems.map((item) => {
            const itemKey = String(item?.key || '').trim();
            const isDone = String(item?.status || '').toLowerCase() === 'done';
            const actions = checklist.getItemActions(item);
            const resources = checklist.getItemResources(item);

            return (
              <div className="node-mission-task-checklist-item" key={itemKey}>
                <Checkbox
                  label={String(item?.text || '').trim() || 'Item'}
                  size="16"
                  strikethrough
                  value={isDone}
                  onChange={() => checklist.handleToggleItem(itemKey)}
                />
                {actions.length > 0 || resources.length > 0 ? (
                  <div className="node-mission-task-checklist-item-actions">
                    {actions.map((action) => {
                      const actionLabel = getChecklistActionButtonLabel(
                        action,
                        checklist.modalFields,
                      );
                      const isRequiredValue = action.requireValue === true
                        && isChecklistSetFieldActionType(action?.type);
                      return (
                        <Button
                          key={action.id}
                          backgroundColor={theme.backgroundMuted}
                          foregroundColor={theme.foregroundMuted}
                          hoverBackgroundColor={theme.buttonBackgroundColor}
                          hoverForegroundColor={theme.buttonHoverLabelColor}
                          enabled={checklist.isActionEnabled(action)}
                          iconKey={getChecklistActionIconKey(action)}
                          iconSize="13px"
                          iconColor={theme.foregroundMuted}
                          label={isRequiredValue ? `${actionLabel} *` : actionLabel}
                          title={isRequiredValue
                            ? 'Required — set a value before checking this item off'
                            : undefined}
                          onClick={() => checklist.handleOpenAction(action, itemKey)}
                        />
                      );
                    })}
                    {resources.map((resource) => {
                      const liveNode = checklist.resourceNodesByKey.get(resource.nodeKey);
                      const typeDefForResource = liveNode?.type
                        ? getNodeType(liveNode.type)
                        : null;
                      const { iconKey } = resolveNodeIcon({
                        node: liveNode || null,
                        typeDef: typeDefForResource,
                      });
                      const label = String(
                        liveNode?.title || resource.title || 'Resource',
                      ).trim() || 'Resource';

                      return (
                        <Button
                          key={resource.id}
                          backgroundColor={theme.backgroundMuted}
                          foregroundColor={theme.foregroundMuted}
                          hoverBackgroundColor={theme.buttonBackgroundColor}
                          hoverForegroundColor={theme.buttonHoverLabelColor}
                          iconKey={iconKey || 'bookmark'}
                          iconSize="13px"
                          iconColor={theme.foregroundMuted}
                          label={label}
                          onClick={() => checklist.handleOpenResource(resource)}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }) : (
            <div
              className="node-mission-task-checklist-empty"
              style={{ color: theme.foregroundMuted }}
            >
              {checklist.hasChecklistSnapshot ? 'Preparing checklist…' : 'Loading checklist…'}
            </div>
          )}
        </div>
      ) : null}

      <Confirm
        confirmLabel="Mark complete"
        isOpen={checklist.confirmOpen}
        message="All checklist items are done. Would you like to mark this step complete?"
        secondaryLabel="Not yet"
        setOpen={checklist.setConfirmOpen}
        title="Checklist complete"
        onConfirm={checklist.handleConfirmComplete}
        onSecondary={checklist.handleDismissComplete}
      />

      <NodeMissionTaskChecklistActionModal
        fieldDef={checklist.activeAction?.fieldDef || null}
        hostNode={hostNode}
        isOpen={Boolean(checklist.activeAction)}
        onClose={checklist.handleCloseAction}
        onHostNodeChange={onHostNodeChange}
      />

      <NodeHiddenFileInputs
        documentInputRef={checklist.documentInputRef}
        filesInputRef={checklist.filesInputRef}
        photoInputRef={checklist.photoInputRef}
        videoInputRef={checklist.videoInputRef}
        onDocumentSelect={checklist.handleDocumentSelect}
        onFilesSelect={checklist.handleFilesSelect}
        onPhotoSelect={checklist.handlePhotoSelect}
        onVideoSelect={checklist.handleVideoSelect}
      />

      <CameraModal
        contentType={checklist.cameraContentType}
        isOpen={checklist.cameraOpen}
        setOpen={checklist.setCameraOpen}
        onContentCreated={checklist.handleCameraContentCreated}
      />
    </div>
  );
});

NodeMissionTaskChecklist.displayName = 'NodeMissionTaskChecklist';

export default NodeMissionTaskChecklist;
